import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE_URL, DEFAULT_OG_IMAGE } from "../data/seo";

const SEO_ATTR = "data-seo-managed";

const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(SEO_ATTR, "true");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"][${SEO_ATTR}]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute(SEO_ATTR, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
};

const writeMetaTags = ({ title, description, keywords, noindex, type, url, image }) => {
  if (title) document.title = title;
  upsertMeta('meta[name="description"]', { name: "description", content: description || "" });
  if (keywords) upsertMeta('meta[name="keywords"]', { name: "keywords", content: keywords });
  upsertMeta('meta[name="robots"]', { name: "robots", content: noindex ? "noindex,nofollow" : "index,follow" });

  upsertMeta('meta[property="og:title"]', { property: "og:title", content: title || "" });
  upsertMeta('meta[property="og:description"]', { property: "og:description", content: description || "" });
  upsertMeta('meta[property="og:type"]', { property: "og:type", content: type });
  upsertMeta('meta[property="og:url"]', { property: "og:url", content: url });
  upsertMeta('meta[property="og:image"]', { property: "og:image", content: image });
  upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "Smiles by Dr. K" });

  upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
  upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title || "" });
  upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description || "" });
  upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });

  upsertLink("canonical", url);
};

const writeJsonLd = (schemas) => {
  document.head
    .querySelectorAll(`script[type="application/ld+json"][${SEO_ATTR}]`)
    .forEach((s) => s.remove());
  schemas.filter(Boolean).forEach((data) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute(SEO_ATTR, "true");
    s.textContent = JSON.stringify(data);
    document.head.appendChild(s);
  });
};

export const Seo = ({
  title,
  description,
  image = DEFAULT_OG_IMAGE,
  keywords,
  noindex = false,
  type = "website",
  schemas = [],
}) => {
  const { pathname } = useLocation();
  const url = `${SITE_URL}${pathname}`;
  const schemasKey = JSON.stringify(schemas);

  // Effect 1 — meta tags (cheap, runs on most prop changes)
  useEffect(() => {
    writeMetaTags({ title, description, keywords, noindex, type, url, image });
  }, [title, description, keywords, noindex, type, url, image]);

  // Effect 2 — JSON-LD (only when schemas content changes)
  useEffect(() => {
    writeJsonLd(schemas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemasKey]);

  return null;
};
